import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Key, Copy, Eye, EyeOff, Search } from 'lucide-react';
import { toast } from 'sonner';

export const EmployeesTab = ({ employees, loadData }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', employee_id: '' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [searchEmp, setSearchEmp] = useState('');

  const filteredEmployees = employees.filter(emp => {
    const q = searchEmp.toLowerCase();
    return !q || emp.full_name?.toLowerCase().includes(q) ||
           emp.employee_id?.toLowerCase().includes(q) ||
           emp.username?.toLowerCase().includes(q);
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Копирано!');
  };

  const closeCredentialsDialog = () => {
    setShowDialog(false);
    setCredentials(null);
    loadData();
  };

  const openPasswordDialog = (employee) => {
    setSelectedEmployee(employee);
    setNewPassword('');
    setShowNewPassword(false);
    setShowPasswordDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/employees`, formData);
      setCredentials(response.data);
    } catch {
      toast.error('Грешка при създаване на служител');
    }
  };

  const handlePasswordUpdate = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Паролата трябва да е поне 4 символа');
      return;
    }
    try {
      await axios.put(`${API}/employees/${selectedEmployee.id}/password`, { new_password: newPassword });
      toast.success('Паролата е променена успешно');
      setShowPasswordDialog(false);
      loadData();
    } catch {
      toast.error('Грешка при промяна на парола');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този служител?')) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Служителят е изтрит');
      loadData();
    } catch {
      toast.error('Грешка при изтриване');
    }
  };

  return (
    <>
      <Card className="p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading-font text-xl font-bold uppercase">Служители</h2>
          <Button data-testid="add-employee-button" onClick={() => { setFormData({ full_name: '', employee_id: '' }); setShowDialog(true); }} className="bg-red-600 hover:bg-red-700 font-bold uppercase text-sm">
            <Plus className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Добави служител</span><span className="sm:hidden">+ Нов</span>
          </Button>
        </div>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="employee-search"
            placeholder="Търси по име, номер или потребител..."
            value={searchEmp}
            onChange={(e) => setSearchEmp(e.target.value)}
            className="pl-9 border-2"
          />
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs">Пълно Име</TableHead>
              <TableHead className="uppercase text-xs">Служ. номер</TableHead>
              <TableHead className="uppercase text-xs">Потреб. Име</TableHead>
              <TableHead className="uppercase text-xs">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-600">Няма добавени служители</TableCell></TableRow>
            ) : filteredEmployees.map((emp) => (
              <TableRow key={emp.id}>
                <TableCell className="font-medium">{emp.full_name}</TableCell>
                <TableCell className="mono-font">{emp.employee_id}</TableCell>
                <TableCell className="mono-font">{emp.username}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button data-testid={`change-password-${emp.id}`} onClick={() => openPasswordDialog(emp)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Смяна на парола">
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`delete-employee-${emp.id}`} onClick={() => handleDelete(emp.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      {/* New Employee Dialog */}
      <Dialog open={showDialog && !credentials} onOpenChange={(open) => !open && setShowDialog(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="heading-font text-2xl uppercase">Нов служител</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm uppercase tracking-wide mb-1 block">Пълно Име *</Label>
              <Input data-testid="employee-name-input" required value={formData.full_name || ''} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="border-2" />
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-1 block">Служебен номер *</Label>
              <Input data-testid="employee-id-input" required value={formData.employee_id || ''} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} className="border-2" />
            </div>
            <Button data-testid="submit-form-button" type="submit" className="w-full bg-red-600 hover:bg-red-700 h-12 font-bold uppercase">Създай</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog (shown once after creation) */}
      <Dialog open={!!credentials} onOpenChange={() => closeCredentialsDialog()}>
        <DialogContent>
          <DialogHeader><DialogTitle className="heading-font text-2xl uppercase">Данни за вход</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border-2 border-amber-200 rounded-sm p-4">
              <p className="text-amber-800 font-medium text-center">ВАЖНО: Запазете тези данни! Те няма да бъдат показани отново.</p>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Потребителско Име</Label>
              <div className="flex gap-2">
                <Input value={credentials?.username || ''} readOnly className="mono-font font-bold text-lg border-2" />
                <Button data-testid="copy-username-button" onClick={() => copyToClipboard(credentials?.username)} variant="outline" className="border-2"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Парола</Label>
              <div className="flex gap-2">
                <Input value={credentials?.password || ''} readOnly className="mono-font font-bold text-lg border-2" />
                <Button data-testid="copy-password-button" onClick={() => copyToClipboard(credentials?.password)} variant="outline" className="border-2"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <Button data-testid="close-credentials-button" onClick={closeCredentialsDialog} className="w-full bg-red-600 hover:bg-red-700 h-12 font-bold uppercase">Затвори</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="heading-font text-2xl uppercase">Промяна на парола</DialogTitle></DialogHeader>
          {selectedEmployee && (
            <div className="space-y-4">
              <div className="bg-slate-100 border border-slate-200 rounded-sm p-4">
                <p className="text-sm text-slate-600 mb-1">Служител</p>
                <p className="font-bold">{selectedEmployee.full_name}</p>
                <p className="text-sm text-slate-600 mono-font">{selectedEmployee.username}</p>
              </div>
              <div>
                <Label className="text-sm uppercase tracking-wide mb-2 block">Нова Парола *</Label>
                <div className="relative">
                  <Input
                    data-testid="new-password-input"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mono-font font-bold text-base border-2 pr-12"
                    placeholder="Въведете нова парола"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <Button data-testid="update-password-button" onClick={handlePasswordUpdate} className="flex-1 bg-red-600 hover:bg-red-700 h-12 font-bold uppercase">Запази</Button>
                <Button onClick={() => setShowPasswordDialog(false)} variant="outline" className="flex-1 h-12 border-2 font-semibold uppercase">Отказ</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
